/**
 * Wire shapes for the admin JSON API (backend `routes/api.php`). Hand-kept in
 * step with the `App\Http\Controllers\Api\*` presenters — there is no codegen
 * yet (blueprint §10 defers the OpenAPI dump).
 */

export interface ServerCapabilities {
  tier: string;
  push: boolean;
  aspBasePath?: string;
  publicApi?: boolean;
  publicApiBasePath?: string;
  webhooks?: boolean;
  webhookEvents?: { name: string; label: string }[];
  modules?: boolean;
  billing?: boolean;
  /** Whether this server can sign portable CMS player licence keys. */
  portableLicenses?: boolean;
  /** Whether customers can buy portable player licences here (Midtrans). */
  portableLicenseSales?: boolean;
  /** OAuth web client id for "Sign in with Google" on the login screen; null when it's off. */
  googleClientId?: string | null;
  /** Self-service sign-up is open: the login screen links to /signup. */
  signup?: boolean;
  /** Akarya Box with no owner yet: the console opens /setup instead of the login form. */
  boxSetup?: boolean;
  /** Akarya Box: Settings → Backup exists (owners only). */
  boxBackup?: boolean;
  /** Akarya Box simple mode: the console shows only screens, layouts, media and SOS. */
  simple?: boolean;
  /** Features a box turns off (all true elsewhere). The server refuses them too. */
  sso?: boolean;
  roles?: boolean;
  apiTokens?: boolean;
  impersonation?: boolean;
  branding?: boolean;
  moduleRegistry?: boolean;
}

/** What a box backup holds, as the Backup screen shows it. */
export interface BoxBackupSummary {
  createdAt: string | null;
  placeName: string | null;
  counts: { displays?: number; layouts?: number; media?: number } | null;
  mediaBytes: number | null;
  caIncluded: boolean;
  stagedAt: string | null;
}

export interface BoxBackupState {
  staged: BoxBackupSummary | null;
  /** The server knows where the box keeps its certificate authority. */
  caConfigured: boolean;
}

export interface BoxBackupMade {
  id: string;
  name: string;
  size: number;
  encrypted: boolean;
  backup: BoxBackupSummary;
}

/** An enabled connection, as offered to the (unauthenticated) login screen. */
export interface SsoConnectionOption {
  id: string;
  name: string;
  providerHint: 'google' | 'microsoft' | 'okta' | 'generic';
}

export type SsoDefaultRole = 'admin' | 'editor' | 'viewer';

export type SsoProtocol = 'oidc' | 'saml';

/**
 * The full admin-managed shape (ADR-0017 OIDC, ADR-0018 SAML) — never
 * carries the OIDC client secret itself. The SAML IdP certificate is not a
 * secret (it's the IdP's public signing cert) so it's returned as-is.
 */
export interface SsoConnection {
  id: string;
  name: string;
  protocol: SsoProtocol;
  providerHint: 'google' | 'microsoft' | 'okta' | 'generic';
  issuer: string;
  clientId: string;
  hasSecret: boolean;
  samlIdpEntityId: string | null;
  samlIdpSsoUrl: string | null;
  samlIdpCertificate: string | null;
  samlNameIdFormat: string | null;
  samlSpMetadataUrl: string | null;
  samlSpAcsUrl: string | null;
  /** ADR-0027: accept unsolicited (IdP dashboard tile) sign-ins. Off by default. */
  samlAllowIdpInitiated: boolean;
  /** ADR-0027: sign AuthnRequests with this connection's SP key. */
  samlSignRequests: boolean;
  /** Public SP signing certificate (PEM), also in the SP metadata. The private key is never returned. */
  samlSpCertificate: string | null;
  defaultRole: SsoDefaultRole;
  allowJit: boolean;
  enforced: boolean;
  domainHint: string | null;
  enabled: boolean;
  /** SCIM user lifecycle sync (ADR-0025) — independent of `enabled` (login). */
  scimEnabled: boolean;
  hasScimToken: boolean;
  scimBaseUrl: string;
  lastUsedAt: string | null;
}

/** Parsed IdP metadata (ADR-0027) — pre-fills the connection form, stores nothing. */
export interface SamlMetadataImport {
  entityId: string;
  ssoUrl: string;
  certificate: string;
  nameIdFormat: string | null;
  warnings: string[];
}

export interface ModuleCatalogEntry {
  slug: string;
  name: string;
  version: string;
  kind: 'static' | 'media' | 'data';
  source: 'builtin' | 'custom' | 'registry';
  status: string;
  description: string | null;
  author: string | null;
  settingsSchema: Record<string, unknown> | null;
  defaults: Record<string, unknown>;
  installed: boolean;
  enabled: boolean;
  installedVersion: string | null;
  settings?: Record<string, unknown> | null;
  feeds?: ModuleFeed[] | null;
}

export interface ModuleFeed {
  configHash: string;
  providerType: string;
  status: 'pending' | 'ok' | 'error';
  error: string | null;
  fetchedAt: string | null;
  expiresAt: string | null;
}

export interface RegistryModuleEntry {
  slug: string;
  name: string;
  description: string | null;
  author: string | null;
  homepage: string | null;
  kind: 'static' | 'media' | 'data';
  channels: Record<string, string>;
  versions: string[];
  fetchedAt: string | null;
  installed: boolean;
  installedVersion: string | null;
}

export interface RegistryListResponse {
  enabled: boolean;
  signingConfigured: boolean;
  data: RegistryModuleEntry[];
}

export interface NativeModule {
  slug: string;
  label: string;
  kind: string;
}

export interface ModuleCatalogResponse {
  data: ModuleCatalogEntry[];
  native: NativeModule[];
}

export interface ApiToken {
  id: string;
  name: string;
  abilities: Permission[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string | null;
}

export interface CreatedApiToken {
  data: ApiToken;
  plainTextToken: string;
}

export type PlayerLicenseState = 'licensed' | 'trial' | 'expired' | 'unknown';

/** A portable player and its licence state (`GET /api/licenses`). */
export interface LicensedPlayer {
  displayId: string;
  name: string;
  /** Null until the player's CMS app has reported it. */
  licenseId: string | null;
  state: PlayerLicenseState;
  reportedAt: string | null;
}

export type LicenseOrderStatus = 'pending' | 'paid' | 'failed' | 'expired' | 'cancelled';

/** One self-service licence order: paid through Midtrans or Google Play, or free (issued at once). */
export interface LicenseOrder {
  id: string;
  orderRef: string;
  status: LicenseOrderStatus;
  provider: 'midtrans' | 'google_play' | 'free';
  licenseIds: string[];
  quantity: number;
  unitPrice: number;
  grossAmount: number;
  currency: string;
  paymentType: string | null;
  /** The Midtrans payment page, while the order is still pending. */
  redirectUrl: string | null;
  createdAt: string | null;
  paidAt: string | null;
}

export interface LicenseOverview {
  data: LicensedPlayer[];
  keys: { licenseId: string; licenseKey: string }[];
  orders: LicenseOrder[];
  /** `free`: checkout issues keys at once, at no charge (unitPrice is then 0). */
  sales: { available: boolean; unitPrice: number; currency: string; free?: boolean };
}

export interface WebhookEndpoint {
  id: string;
  url: string;
  description: string | null;
  events: string[];
  enabled: boolean;
  consecutiveFailures: number;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
  disabledAt: string | null;
  createdAt: string | null;
  secret?: string;
}

export type WebhookDeliveryStatus = 'pending' | 'delivered' | 'failed' | 'dead';

export interface WebhookDelivery {
  id: string;
  event: string;
  status: WebhookDeliveryStatus;
  attempts: number;
  responseStatus: number | null;
  error: string | null;
  nextAttemptAt: string | null;
  deliveredAt: string | null;
  createdAt: string | null;
}

export interface Paginated<T> {
  data: T[];
  meta: { page: number; perPage: number; total: number; lastPage: number };
}

export interface XiboImportReport {
  dryRun: boolean;
  ok: boolean;
  layoutId: string | null;
  layoutName: string | null;
  regionsMapped: number;
  widgetsMapped: number;
  widgetsSkipped: number;
  media: { created: number; reused: number; missing: number };
  modules: Record<string, number>;
  warnings: string[];
  errors: string[];
}

export interface DisplayImportResult {
  dryRun: boolean;
  created: number;
  skipped: number;
  names: string[];
}

export interface Branding {
  productName: string;
  accentColor: string;
  supportUrl: string | null;
  logoUrl: string | null;
  loginBackgroundUrl: string | null;
  playerSplashUrl: string | null;
}

export type BrandingAssetKind = 'logo' | 'login-background' | 'player-splash';

export interface BrandingSettings {
  settings: {
    productName: string | null;
    accentColor: string | null;
    supportUrl: string | null;
    emailFromName: string | null;
    emailFooter: string | null;
  };
  defaults: { productName: string; accentColor: string };
  assets: { logoUrl: string | null; loginBackgroundUrl: string | null; playerSplashUrl: string | null };
}

/** `support` (operator impersonation) is only offered in an operator organisation. */
export type SystemRole = 'owner' | 'admin' | 'demo_admin' | 'editor' | 'viewer' | 'support';

/** A permission slug from the backend catalogue (`config/permissions.php`). */
export type Permission = string;

export interface CurrentUser {
  id: string;
  name: string;
  email: string;
  role: SystemRole;
  roleLabel: string;
  permissions: Permission[];
  tenantId: string;
  /** Whether this user may impersonate customer users (operator staff only). */
  canImpersonate: boolean;
  /** Whether this user may mint portable player licence keys (operator owners only). */
  canIssueLicenses?: boolean;
  /** The operator signed in as this user, while an impersonation is running. */
  impersonator: { id: string; name: string; email: string } | null;
  impersonationExpiresAt: string | null;
  /**
   * A demo administrator (backend `App\Support\DemoAdmin`): the console only shows the layouts, media,
   * displays and groups it created or was shared, and the rest of the console is closed to it.
   */
  restrictedView: boolean;
}

/** A portable player licence key minted by an operator (`/api/settings/portable-licenses`). */
export interface PortableLicense {
  id: string;
  licensee: string;
  licenseIds: string[];
  issuedAt: string;
  expiresAt: string | null;
  licenseKey: string;
  issuedBy: { id: string; name: string } | null;
  /** Customer the key is filed under, when a linked player's account owns it. */
  tenantName?: string | null;
  /** `cms`: delivered through the customer's portable CMS; otherwise pasted in via the player's QR form. */
  delivery?: 'cms' | 'manual';
}

/** What an admin can share with demo administrators (`/api/shares/{type}/{id}`). */
export type ShareableType = 'layout' | 'media' | 'display' | 'display_group';

/** A demo administrator an item can be shared with (`GET /api/demo-admins`). */
export interface DemoAdminUser {
  id: number;
  name: string;
  email: string;
}

/** A customer user an operator may impersonate (`GET /api/impersonation/users`). */
export interface ImpersonationCandidate {
  id: string;
  name: string;
  email: string;
  roleLabel: string;
  tenant: { id: string; name: string; slug: string } | null;
}

export interface Role {
  id: string;
  name: string;
  label: string;
  permissions: Permission[];
  isSystem: boolean;
  userCount: number;
}

export interface PermissionGroup {
  group: string;
  permissions: { slug: string; label: string }[];
}

export interface PermissionCatalogue {
  data: PermissionGroup[];
  systemRoles: { name: SystemRole; label: string; permissions: Permission[] }[];
}

export interface ConsoleUser {
  id: string;
  name: string;
  email: string;
  role: SystemRole;
  roleId: string | null;
  roleLabel: string;
  permissions: Permission[];
  lastLoginAt: string | null;
}

export interface InvitedUser extends ConsoleUser {
  inviteUrl: string;
}

export interface AuditEntry {
  id: string;
  action: string;
  actorType: 'user' | 'system' | 'display';
  actorId: string | null;
  actorLabel: string | null;
  targetType: string | null;
  targetId: string | null;
  targetLabel: string | null;
  summary: string | null;
  changes: Record<string, [unknown, unknown] | unknown> | null;
  ip: string | null;
  userAgent: string | null;
  requestId: string | null;
  at: string;
}

export interface AuditPage {
  data: AuditEntry[];
  nextCursor: string | null;
}

export interface ReportSummary {
  displays: { total: number; online: number };
  playsToday: number;
  layoutsPublished: number;
  mediaCount: number;
  topFaults: { code: string; count: number }[];
  activeAlerts: number;
}

export type PopGroupBy = 'day' | 'display' | 'layout' | 'media';

export interface PopRow {
  key: string;
  label: string;
  plays: number;
  playsInFull: number;
  completionRate: number;
  durationS: number;
}

export interface PopReport {
  from: string;
  to: string;
  groupBy: PopGroupBy;
  source: 'rollup' | 'raw' | 'mixed';
  rows: PopRow[];
}

export interface AvailabilityRow {
  displayId: string;
  name: string;
  lastSeenAt: string | null;
  online: boolean;
  pollsSeen: number;
  pollsExpected: number;
  availabilityPct: number;
}

export interface AvailabilityReport {
  from: string;
  to: string;
  displays: AvailabilityRow[];
}

export type AlertSeverity = 'info' | 'warning' | 'critical';
export type AlertState = 'open' | 'acknowledged' | 'resolved';
export type AlertKind = 'display_offline' | 'fault_present' | 'status_error';

export interface AlertItem {
  id: string;
  ruleId: string;
  ruleLabel: string | null;
  kind: AlertKind | null;
  displayId: string | null;
  severity: AlertSeverity;
  state: AlertState;
  summary: string;
  context: Record<string, unknown> | null;
  openedAt: string;
  lastSeenAt: string;
  acknowledgedAt: string | null;
  resolvedAt: string | null;
}

export interface AlertList {
  data: AlertItem[];
  openCount: number;
}

export interface AlertRule {
  id: string;
  kind: AlertKind;
  label: string;
  severity: AlertSeverity;
  enabled: boolean;
  params: Record<string, number>;
  channelIds: string[];
}

export interface AlertChannel {
  id: string;
  type: 'in_app' | 'email' | 'webhook';
  label: string;
  enabled: boolean;
  config: Record<string, unknown>;
}

export type DisplayStatus = 'pending' | 'active' | 'disabled';
export type Orientation = 'landscape' | 'portrait';

export interface Display {
  id: string;
  name: string;
  status: DisplayStatus;
  credentialMode: string | null;
  /** Set for a player linked from the portable CMS app. */
  portablePlayerId?: string | null;
  portableLicenseId?: string | null;
  /** Derived: a key bought for this player counts even before it re-syncs. */
  portableLicenseState?: PlayerLicenseState | null;
  shell: string | null;
  model: string | null;
  lastSeenAt: string | null;
  lastSyncAt: string | null;
  currentLayoutId: string | null;
  currentScheduleId: string | null;
  faults: string[];
  defaultLayoutId: string | null;
  orientation: Orientation;
  settings: Record<string, unknown> | null;
  cacheBytesUsed: number | null;
  screenshotAt: string | null;
}

/** A named set of displays retargeted together (`POST /api/display-groups/{id}/layout`). */
export interface DisplayGroup {
  id: string;
  name: string;
  description: string | null;
  /** The layout last applied to the whole group, if any. */
  layoutId: string | null;
  displayIds: string[];
  displayCount: number;
}

export interface ClaimCodeResponse {
  claimCode: string;
  expiresInMinutes: number;
}

/** An open pairing request a player is showing (spec §2.3). */
export interface PairingInfo {
  device: {
    shell?: string;
    shellVersion?: string;
    model?: string;
    os?: string;
    screenWidth?: number;
    screenHeight?: number;
  };
  expiresAt: string;
}

export type CommandType =
  | 'reboot'
  | 'restartApp'
  | 'screenshot'
  | 'clearCache'
  | 'resync'
  | 'rotateKey'
  | 'setLogLevel'
  | 'identify';

/** A node in the shared media/layout folder tree (ADR-0024). */
export interface FolderNode {
  id: string;
  parentId: string | null;
  name: string;
  path: string;
  mediaCount: number;
  layoutsCount: number;
  childrenCount: number;
}

/** One user's extra, folder-scoped permissions — additive to their role. */
export interface FolderGrantEntry {
  id: string;
  folderId: string;
  userId: number;
  userName: string | null;
  userEmail: string | null;
  permissions: Permission[];
}

/** The only slugs a folder grant may ever carry (ADR-0024). */
export const FOLDER_SCOPABLE_PERMISSIONS: Permission[] = [
  'media.view',
  'media.manage',
  'layouts.view',
  'layouts.edit',
  'layouts.publish',
];

/** A folder as the media library explorer sees it — readable with `media.view`. */
export interface MediaFolder {
  id: string;
  /** Null for a top-level folder, or for a granted folder whose parent the user can't see. */
  parentId: string | null;
  name: string;
  mediaCount: number;
  childrenCount: number;
}

export interface MediaItem {
  id: string;
  folderId: string | null;
  sha256: string;
  size: number;
  mime: string | null;
  originalName: string | null;
  width: number | null;
  height: number | null;
  duration: number | null;
  url: string;
  /** A small WebP preview for library tiles; null for videos and other non-images. */
  thumbUrl: string | null;
}

export type MediaSort = 'newest' | 'name' | 'size';

/** Which slice of the library to list: everything, unfiled only, or one folder. */
export type MediaScope = { kind: 'all' } | { kind: 'unfiled' } | { kind: 'folder'; id: string };

export interface MediaPage {
  data: MediaItem[];
  hasMore: boolean;
}

export type LayoutStatus = 'draft' | 'published';

export interface Layout {
  id: string;
  folderId: string | null;
  name: string;
  status: LayoutStatus;
  width: number;
  height: number;
  tags: string[];
  publishedHash: string | null;
  publishedVersion: number | null;
}

/** One orientation's SOS layout (`GET /api/sos`). */
export interface SosLayoutRef {
  id: string;
  name: string;
  width: number;
  height: number;
  published: boolean;
}

/**
 * Emergency SOS (ADR-0029): while `active`, every display shows the SOS layout
 * for the orientation it is seen in, over its schedule.
 */
export interface SosState {
  active: boolean;
  activatedAt: string | null;
  activatedBy: { id: number; name: string } | null;
  landscapeLayoutId: string | null;
  portraitLayoutId: string | null;
  layouts: Record<Orientation, SosLayoutRef | null>;
  /** Non-disabled displays by the orientation SOS is shown in (mount rotation included). */
  displays: Record<Orientation, number>;
}

export interface LayoutDetail extends Layout {
  latestDocument: LayoutDocument | null;
}

/** ADR-0021: a tenant-managed price list rendered by the `menuboard` widget module. */
export interface MenuBoardSummary {
  id: string;
  name: string;
  currency: string;
  categoryCount: number;
  productCount: number;
}

export interface MenuProduct {
  id: string;
  name: string;
  description: string | null;
  price: number | null;
  available: boolean;
}

export interface MenuCategory {
  id: string;
  name: string;
  products: MenuProduct[];
}

export interface MenuBoard {
  id: string;
  name: string;
  currency: string;
  categories: MenuCategory[];
}

/** A product/category as the console edits it, before the replace-the-tree save. */
export interface MenuProductDraft {
  name: string;
  description: string | null;
  price: number | null;
  available: boolean;
}

export interface MenuCategoryDraft {
  name: string;
  products: MenuProductDraft[];
}

/** The reserved slugs the runtime renders natively. Marketplace modules
 * (ADR-0015) put their own slug in `LayoutWidget.module`, hence the wider type. */
export type LayoutModule =
  | 'image'
  | 'video'
  | 'audio'
  | 'text'
  | 'html'
  | 'web'
  | 'clock'
  | 'weather'
  | 'rss'
  | 'pdf'
  | 'embedded';

export interface LayoutWidgetMedia {
  sha256: string;
  mime?: string;
  role?: 'primary' | 'poster' | 'font' | 'html-bundle' | 'attachment';
}

export type LayoutActionType = 'navigateLayout' | 'previousLayout';

/** A touch action on a widget (ADR-0020). */
export interface LayoutAction {
  trigger: 'tap';
  type: LayoutActionType;
  layoutId?: string;
  returnAfterS?: number;
}

export interface LayoutWidget {
  id: string;
  module: LayoutModule | string;
  enabled?: boolean;
  duration?: number;
  useMediaDuration?: boolean;
  media?: LayoutWidgetMedia[];
  options?: Record<string, unknown>;
  actions?: LayoutAction[];
}

export interface LayoutRect {
  unit?: 'px' | 'percent';
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface LayoutRegion {
  id: string;
  name?: string;
  rect: LayoutRect;
  zIndex?: number;
  loop?: boolean;
  playlist: { id?: string; name?: string; items: LayoutWidget[] };
}

export interface LayoutDocument {
  schemaVersion: 1;
  id: string;
  name?: string;
  tags?: string[];
  dimensions: { width: number; height: number; orientation?: Orientation };
  background?: { color?: string; mediaSha256?: string; mediaMime?: string; fit?: string };
  interactive?: { timeoutS?: number };
  regions: LayoutRegion[];
}

export interface Daypart {
  days: string;
  on: string;
  off: string;
}

export type ScheduleLayerKind = 'default' | 'scheduled' | 'interrupt';

export interface ScheduleLayer {
  id: string;
  priority: number;
  kind: ScheduleLayerKind;
  layoutId: string;
  startsAt: string | null;
  endsAt: string | null;
  dayparts: Daypart[];
}

export interface ScheduleLayersResponse {
  defaultLayoutId: string | null;
  layers: ScheduleLayer[];
}

/** One layer as the console edits it, before it is sent to the replace endpoint. */
// SaaS billing (ADR-0019). Cloud-tier-only — gated by `ServerCapabilities.billing`.

export interface BillingPlan {
  slug: string;
  label: string;
  maxDisplays: number | null;
  features: Record<string, boolean>;
  hasMonthlyPrice: boolean;
  hasYearlyPrice: boolean;
}

export type BillingStatus = 'none' | 'trialing' | 'active' | 'past_due' | 'canceled' | 'incomplete' | 'incomplete_expired';

export interface BillingSummary {
  planSlug: string | null;
  planLabel: string | null;
  status: BillingStatus;
  interval: 'month' | 'year' | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  seatsUsed: number;
  seatsLimit: number | null;
}

export interface BillingHistoryEvent {
  id: string;
  type: string;
  summary: string;
  hostedInvoiceUrl: string | null;
  amountCents: number | null;
  currency: string | null;
  occurredAt: string;
}

export interface BillingOverview {
  summary: BillingSummary;
  history: Paginated<BillingHistoryEvent>;
}

export interface ScheduleLayerDraft {
  kind: 'scheduled' | 'interrupt';
  priority: number;
  layoutId: string;
  startsAt: string | null;
  endsAt: string | null;
  dayparts: Daypart[];
}
